import { authApiClient } from '../authApiClient';
import { 
  TokenRefreshRequest, 
  TokenIntrospectRequest,
  InitAuthRequest,
  VerifyLoginSmsOtpRequest
} from '../models/Auth';
import { extractErrorMessage } from '../utils/errorHandler';
import { setLoggedInCookie } from '../utils/cookieUtils';
import { getAnalyticsPayload } from '../utils/analytics';

export interface UserRoles {
  roles: string[];
}

class AuthService {
  private static instance: AuthService;
  private isLoggedIn: boolean = false;
  private roles: string[] = [];
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private phoneNumber: string | null = null;
  private userId: string | null = null;
  private refreshTimeout: NodeJS.Timeout | null = null;
  private assumedOrganizationId: string | null = null;
  private assumedOrganizationName: string | null = null;
  private assumedUserId: string | null = null;
  private assumedUserName: string | null = null;
  private initializationPromise: Promise<void> | null = null;

  
  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private constructor() {
    // Don't call initializeFromStorage here - it's async now
    this.initializeFromStorageSync();
  }

  private initializeFromStorageSync() {
    // Initialize basic storage items synchronously
    this.accessToken = localStorage.getItem('access_token');
    this.refreshToken = localStorage.getItem('refresh_token');
    this.phoneNumber = localStorage.getItem('phone_number');
    this.userId = localStorage.getItem('user_id');
    this.assumedOrganizationId = localStorage.getItem('assumed_organization_id');
    this.assumedOrganizationName = localStorage.getItem('assumed_organization_name');
    this.assumedUserId = localStorage.getItem('assumed_user_id');
    this.assumedUserName = localStorage.getItem('assumed_user_name');
    
    // Load roles from localStorage immediately for instant navigation rendering
    // This prevents empty sidebar on initial load
    const storedRoles = localStorage.getItem('user_roles');
    if (storedRoles) {
      try {
        this.roles = JSON.parse(storedRoles);
      } catch (e) {
        console.error('Failed to parse stored roles:', e);
        this.roles = [];
      }
    }
    
    // Set initial state based on what we have
    // Don't set isLoggedIn to true here - let the async initialization handle it
    // This prevents the flash of "logged in" state when tokens might be expired
    this.isLoggedIn = false;
  }

  public async initialize(): Promise<void> {
    // Idempotent: ProtectedRoute and DashboardRoutes may both await this.
    if (!this.initializationPromise) {
      this.initializationPromise = this.initializeFromStorage();
    }
    await this.initializationPromise;
  }

  /**
   * OTP verify (and similar flows) write tokens to localStorage without updating this instance.
   * Call after those writes so ProtectedRoute / SPA navigations see a logged-in session.
   */
  public syncSessionFromStorage(options?: { scheduleRefresh?: boolean }): boolean {
    // Drop stale admin "assume" headers so post-login API calls aren't 401'd.
    this.clearAssumedOrganization();
    this.clearAssumedUser();

    this.accessToken = localStorage.getItem('access_token');
    this.refreshToken = localStorage.getItem('refresh_token');
    this.phoneNumber = localStorage.getItem('phone_number');
    this.userId = localStorage.getItem('user_id');

    const storedRoles = localStorage.getItem('user_roles');
    if (storedRoles) {
      try {
        this.roles = JSON.parse(storedRoles);
      } catch {
        this.roles = [];
      }
    }

    if (this.accessToken && this.refreshToken) {
      this.isLoggedIn = true;
      setLoggedInCookie(true);
      // Treat as initialized so ProtectedRoute doesn't await a second full storage init
      // (that briefly blanked the onboarding screen after username → plan).
      this.initializationPromise = Promise.resolve();
      if (options?.scheduleRefresh !== false) {
        this.scheduleTokenRefresh();
      }
      return true;
    }

    this.isLoggedIn = false;
    setLoggedInCookie(false);
    return false;
  }

  private async initializeFromStorage(): Promise<void> {
    // Check if we can potentially restore the session
    if (this.refreshToken && this.phoneNumber) {
      // Check if access token is expired or missing
      const isExpired = !this.accessToken || this.isAccessTokenExpired();
      
      if (isExpired) {
        // Access token is expired or missing, attempt refresh
        console.log('🔄 App startup: access token expired/missing, attempting token refresh...');
        this.isLoggedIn = false; // Set to false initially until refresh completes
      } else {
        // Access token appears valid, set logged in and schedule refresh
        console.log('🔄 App startup: access token appears valid, scheduling refresh...');
        this.isLoggedIn = true;
        setLoggedInCookie(true);
        this.scheduleTokenRefresh();
        return; // Exit early since we don't need to refresh immediately
      }
      
      try {
        const success = await this.attemptTokenRefresh();
        if (success) {
          console.log('✅ Token refresh successful during app startup');
          this.isLoggedIn = true;
          setLoggedInCookie(true);
        } else {
          console.log('❌ Token refresh failed during app startup');
          // Don't immediately set isLoggedIn to false on network errors
          // Keep the user logged in if they have valid tokens
          if (this.refreshToken && this.phoneNumber) {
            console.log('⚠️ Keeping user logged in despite refresh failure (network/server error)');
            this.isLoggedIn = true;
            setLoggedInCookie(true);
          } else {
            this.isLoggedIn = false;
            setLoggedInCookie(false);
          }
        }
      } catch (error) {
        console.error('❌ Error during token refresh during app startup:', error);
        // Don't immediately logout on network errors during initialization
        // Keep the user logged in if they have valid tokens
        if (this.refreshToken && this.phoneNumber) {
          console.log('⚠️ Keeping user logged in despite refresh error (network/server error)');
          this.isLoggedIn = true;
          setLoggedInCookie(true);
        } else {
          this.isLoggedIn = false;
          setLoggedInCookie(false);
        }
      }
    } else {
      // No refresh token, definitely not logged in
      this.isLoggedIn = false;
      setLoggedInCookie(false);
    }
  }

  private async attemptTokenRefresh(): Promise<boolean> {
    try {
      console.log('🔄 Attempting automatic token refresh...');
      const success = await this.refreshAccessToken();
      if (success) {
        console.log('✅ Automatic token refresh successful');
        this.isLoggedIn = true;
        return true;
      } else {
        console.log('❌ Automatic token refresh failed');
        // Don't immediately set isLoggedIn to false here - let the caller decide
        // based on whether it's a network error or actual token expiration
        return false;
      }
    } catch (error) {
      console.error('❌ Error during automatic token refresh:', error);
      // Don't immediately set isLoggedIn to false here - let the caller decide
      // based on whether it's a network error or actual token expiration
      return false;
    }
  }

  private scheduleTokenRefresh() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }

    if (!this.accessToken) return;

    const request: TokenIntrospectRequest = {
      accessToken: this.accessToken
    };

    authApiClient.introspectToken(request)
      .then(response => {
        const expirationTime = new Date(response.expiresAt).getTime();
        const currentTime = Date.now();
        const timeUntilExpiry = expirationTime - currentTime;

        // Store expiration time for future reference
        localStorage.setItem('token_expires_at', response.expiresAt);

        // If token is already expired or will expire in less than 5 minutes, refresh immediately
        if (timeUntilExpiry <= 5 * 60 * 1000) {
          this.refreshAccessToken();
        } else {
          // Schedule refresh 5 minutes before expiry
          const refreshDelay = timeUntilExpiry - (5 * 60 * 1000);
          this.refreshTimeout = setTimeout(() => {
            this.refreshAccessToken();
          }, refreshDelay);
        }

        this.roles = response.roles || [];
        // Prefer E.164 with '+'; introspect sometimes returns digits only.
        if (response.phoneNumber?.startsWith('+')) {
          this.phoneNumber = response.phoneNumber;
          localStorage.setItem('phone_number', response.phoneNumber);
        } else if (!this.phoneNumber && response.phoneNumber) {
          this.phoneNumber = response.phoneNumber;
          localStorage.setItem('phone_number', response.phoneNumber);
        }
        // Store roles in localStorage for instant access on next app load
        localStorage.setItem('user_roles', JSON.stringify(this.roles));
        if (response.userId) {
          this.userId = response.userId;
          localStorage.setItem('user_id', response.userId);
        }
      })
      .catch(error => {
        console.error('Token validation failed:', error);
        
        // Don't immediately logout on token validation errors during initialization
        // Instead, try to refresh the token first
        if (this.refreshToken && this.phoneNumber) {
          console.log('🔄 Token validation failed, attempting token refresh...');
          this.attemptTokenRefresh().catch(refreshError => {
            console.error('Token refresh also failed, logging out:', refreshError);
            this.logout();
          });
        } else {
          // Only logout if we don't have refresh credentials
          this.logout();
        }
      });
  }



  /**
   * After profile completion endpoints add or change roles (e.g. HOST, TUTOR),
   * the access token must be refreshed before calling role-gated APIs (e.g. GET /host/organization).
   * Refreshes tokens, awaits introspect to sync `user_roles` in storage, then schedules refresh.
   *
   * Syncs from localStorage first: OTP verify may write tokens directly without updating this instance.
   */
  public async refreshSessionAfterProfileCompletion(): Promise<boolean> {
    this.accessToken = localStorage.getItem('access_token');
    this.refreshToken = localStorage.getItem('refresh_token');
    this.phoneNumber = localStorage.getItem('phone_number');

    if (!this.refreshToken || !this.phoneNumber) {
      console.error('❌ refreshSessionAfterProfileCompletion: missing refresh token or phone number');
      return false;
    }

    try {
      const request: TokenRefreshRequest = {
        refreshToken: this.refreshToken,
        phoneNumber: this.phoneNumber
      };
      const response = await authApiClient.refreshToken(request);
      this.setTokens(response.accessToken, this.refreshToken);

      const introRequest: TokenIntrospectRequest = {
        accessToken: response.accessToken
      };
      const intro = await authApiClient.introspectToken(introRequest);
      this.roles = intro.roles || [];
      localStorage.setItem('user_roles', JSON.stringify(this.roles));
      localStorage.setItem('phone_number', intro.phoneNumber);
      localStorage.setItem('token_expires_at', intro.expiresAt);
      if (intro.userId) {
        this.userId = intro.userId;
        localStorage.setItem('user_id', intro.userId);
      }

      this.scheduleTokenRefresh();
      return true;
    } catch (error) {
      console.error('refreshSessionAfterProfileCompletion failed:', error);

      const errorMessage = error instanceof Error ? error.message : String(error);

      if (errorMessage.includes('Invalid or expired refresh token') ||
          errorMessage.includes('401') ||
          errorMessage.includes('403') ||
          errorMessage.includes('Unauthorized') ||
          errorMessage.includes('Forbidden')) {
        this.logout();
      }
      return false;
    }
  }

  private async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken || !this.phoneNumber) {
      console.log('❌ No refresh token or phone number available');
      this.logout();
      return false;
    }

    try {
      const request: TokenRefreshRequest = {
        refreshToken: this.refreshToken,
        phoneNumber: this.phoneNumber
      };
      const response = await authApiClient.refreshToken(request);
      this.setTokens(response.accessToken, this.refreshToken);
      // Schedule next refresh after successful token refresh
      this.scheduleTokenRefresh();
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      
      // Check if it's a network error or server error vs. invalid token
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      if (errorMessage.includes('Invalid or expired refresh token') || 
          errorMessage.includes('401') || 
          errorMessage.includes('403') ||
          errorMessage.includes('Unauthorized') ||
          errorMessage.includes('Forbidden')) {
        // Token is actually invalid, logout
        console.log('❌ Refresh token is invalid or expired, logging out');
        this.logout();
        return false;
      } else {
        // Network or server error, don't logout immediately
        // The user might still have a valid session
        console.log('⚠️ Token refresh failed due to network/server error, keeping current session');
        // Don't change the login state, just return false
        return false;
      }
    }
  }

  private setTokens(accessToken: string, refreshToken: string) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    this.isLoggedIn = true;
    setLoggedInCookie(true);
  }

  public async initiateLogin(phoneNumber: string): Promise<boolean> {
    try {
      const request: InitAuthRequest = {
        phoneNumber
      };
      await authApiClient.initiateLogin(request);
      // Store the phone number for OTP verification
      localStorage.setItem('phone_number', phoneNumber);
      this.phoneNumber = phoneNumber;
      return true;
    } catch (error) {
      console.error('Login initiation failed:', error);
      const errorMessage = extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  public async verifyLoginOtp(otp: string): Promise<boolean> {
    if (!this.phoneNumber) return false;
    
    try {
      const request: VerifyLoginSmsOtpRequest = {
        otp,
        phoneNumber: this.phoneNumber,
        analytics: getAnalyticsPayload(),
      };
      const response = await authApiClient.verifyLoginOtp(request);
      this.setTokens(response.accessToken, response.refreshToken);
      localStorage.removeItem('organization_type');
      if (response.userId) {
        this.userId = response.userId;
        localStorage.setItem('user_id', response.userId);
      }
      return true;
    } catch (error) {
      console.error('Login verification failed:', error);
      const errorMessage = extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  public logout() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }
    this.initializationPromise = null;
    this.accessToken = null;
    this.refreshToken = null;
    this.phoneNumber = null;
    this.userId = null;
    this.roles = [];
    this.isLoggedIn = false;
    this.assumedOrganizationId = null;
    this.assumedOrganizationName = null;
    this.assumedUserId = null;
    this.assumedUserName = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('phone_number');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_roles'); // Clear cached roles
    localStorage.removeItem('organization_type');
    localStorage.removeItem('assumed_organization_id');
    localStorage.removeItem('assumed_organization_name');
    localStorage.removeItem('assumed_user_id');
    localStorage.removeItem('assumed_user_name');
    setLoggedInCookie(false);
  }

  public getIsLoggedIn(): boolean {
    return this.isLoggedIn;
  }

  public getRoles(): string[] {
    return this.roles;
  }

  public getAccessToken(): string | null {
    return this.accessToken;
  }

  public getPhoneNumber(): string | null {
    return this.phoneNumber;
  }

  public getUserId(): string | null {
    return this.userId;
  }

  public setAssumedOrganization(organizationId: string, organizationName: string) {
    this.assumedOrganizationId = organizationId;
    this.assumedOrganizationName = organizationName;
    localStorage.setItem('assumed_organization_id', organizationId);
    localStorage.setItem('assumed_organization_name', organizationName);
  }

  public clearAssumedOrganization() {
    this.assumedOrganizationId = null;
    this.assumedOrganizationName = null;
    localStorage.removeItem('assumed_organization_id');
    localStorage.removeItem('assumed_organization_name');
  }

  public getAssumedOrganization(): { id: string | null; name: string | null } {
    return {
      id: this.assumedOrganizationId,
      name: this.assumedOrganizationName
    };
  }

  public setAssumedUser(userId: string, userName: string) {
    this.assumedUserId = userId;
    this.assumedUserName = userName;
    localStorage.setItem('assumed_user_id', userId);
    localStorage.setItem('assumed_user_name', userName);
  }

  public clearAssumedUser() {
    this.assumedUserId = null;
    this.assumedUserName = null;
    localStorage.removeItem('assumed_user_id');
    localStorage.removeItem('assumed_user_name');
  }

  public getAssumedUser(): { id: string | null; name: string | null } {
    return {
      id: this.assumedUserId,
      name: this.assumedUserName
    };
  }

  private isAccessTokenExpired(): boolean {
    const expiresAt = localStorage.getItem('token_expires_at');
    if (!expiresAt) {
      return true; // If we don't have expiration info, assume it's expired
    }
    
    const expirationTime = new Date(expiresAt).getTime();
    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;
    
    // Consider token expired if it expires in less than 1 minute
    return timeUntilExpiry <= 60 * 1000;
  }


}

export const authService = AuthService.getInstance();
